import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { MatTableExporterDirective } from 'mat-table-exporter';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { finalize, Observable, Subscription } from 'rxjs';
import { EmployeeService } from 'src/app/employees/employees.service';
import Project from 'src/app/models/Project';
import { TIMESHEET_STATUS, Timesheet } from 'src/app/models/Timesheet';
import { ConfirmationModalComponent } from 'src/app/shared/confirmation-modal/confirmation-modal.component';
import { MinutesToMinutesAndHoursPipe } from 'src/app/shared/pipes/minutes-to-minutes-and-hours.pipe';

import { EMOJIS } from '../../models/EMOJIS';
import { AddTimesheetModalComponent } from '../add-timesheet-modal/add-timesheet-modal.component';
import { CronjobRulesSettingsModalComponent } from '../cronjob-rules-settings-modal/cronjob-rules-settings-modal.component';
import { TimesheetInfoModalComponent } from '../timesheet-info-modal/timesheet-info-modal.component';
import { ChangeTimesheetStatus, RemoveTimesheet } from '../timesheets.actions';
import { TimesheetsService } from '../timesheets.service';
import { TimesheetState } from '../timesheets.state';
import { ReportsModalComponent } from '../reports-modal/reports-modal.component';

interface ReadyToInvoiceStage {
    key: string;
    label: string;
    passed: boolean;
    reason: string;
    details: string[];
}

@Component({
    selector: 'app-timesheets-list',
    templateUrl: './timesheets-list.component.html',
    styleUrls: ['./timesheets-list.component.scss'],
})
export class TimesheetsListComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    @Select(TimesheetState.timesheets) timesheets$: Observable<Timesheet[]>;
    @Select(TimesheetState.selectedDate) selectedDate$: Observable<string>;

    filter;
    isLoading = true;
    sendingReminders = false;

    emojis: EMOJIS[] = [];

    timesheets: Timesheet[] = [];
    allProjects: Project[] = [];
    displayedColumns: string[] = ['status', 'happiness', 'timesheet', 'invoiceReadiness', 'timeslots', 'comments', 'departmentpct', 'totalHours', 'edit'];
    dataSource: MatTableDataSource<Timesheet>;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatTableExporterDirective, { static: true }) exporter: MatTableExporterDirective;

    // Reports moved to modal; no inline toggle state needed

    constructor(
        public modalService: NgbModal,
        private timesheetService: TimesheetsService,
        private toastr: ToastrService,
        private minutesToMinutesAndHours: MinutesToMinutesAndHoursPipe,
        private logger: NGXLogger,
        private store: Store,
        private clipboard: Clipboard,
        private timesheetsService: TimesheetsService,
        private employeeService: EmployeeService
    ) {}

    ngOnInit(): void {
        this.emojis = Object.values(EMOJIS);

        this.subscriptions.add(
            this.selectedDate$.subscribe(() => {
                this.timesheets$.subscribe((t) => {
                    const timesheets = JSON.parse(JSON.stringify(t));
                    this.timesheets = timesheets;
                    /* calculate total worktime */
                    timesheets.forEach((timesheet) => {
                        timesheet.totalHours = this.minutesToMinutesAndHours.transform(Timesheet.getTotalMinutes(timesheet));
                    });

                    /* setup mat-table datasource */
                    this.dataSource = new MatTableDataSource(timesheets);
                    this.dataSource.sort = this.sort;

                    /* setup mat-table filter: parse timesheet to JSON to allow filtering on nested objs */
                    this.dataSource.filterPredicate = (data: Timesheet, filter) => {
                        return (
                            data.employee.firstName.toLowerCase().includes(filter.toLowerCase()) ||
                            data.employee.lastName.toLowerCase().includes(filter.toLowerCase())
                        );
                    };

                    /* setup mat-table sorting: allow sorting on nested objs */
                    this.dataSource.sortingDataAccessor = (item, property) => {
                        if (property.includes('.')) return property.split('.').reduce((o, i) => o[i], item);
                        return item[property];
                    };

                    if (t) {
                        this.isLoading = false;
                    }
                });
            }),
        );

        this.subscriptions.add(
            this.employeeService.getAllProjects(true).subscribe((projects) => {
                this.allProjects = projects || [];
            }),
        );
    }

    getReadyToInvoiceStages(timesheet: Timesheet): ReadyToInvoiceStage[] {
        const stages: ReadyToInvoiceStage[] = [];
        const slots = Array.isArray(timesheet?.timeslots) ? timesheet.timeslots : [];
        const requiredSlots = slots.filter((slot: any) => slot?.project?.timesheetRequired === true);
        const billableSlots = slots.filter((slot: any) => slot?.project?.billable === true);

        const isDone = timesheet?.status?.key === 'DONE';
        stages.push({
            key: '1',
            label: 'Status',
            passed: isDone,
            reason: isDone ? 'Timesheet staat op DONE.' : 'Timesheet staat nog niet op DONE.',
            details: [
                isDone
                    ? 'De timesheet is goedgekeurd.'
                    : `Huidige status: ${timesheet?.status?.key || 'onbekend'}. Zet deze op DONE om te kunnen factureren.`,
            ],
        });

        const totalHours = (this.getTotalMinutes(timesheet) || 0) / 60;
        const requiredHours = Number(timesheet?.hoursRequired || 0);
        const hoursOk = totalHours >= requiredHours;
        stages.push({
            key: '2',
            label: 'Uren',
            passed: hoursOk,
            reason: hoursOk
                ? `Uren OK: ${this.formatHours(totalHours)} / vereist ${this.formatHours(requiredHours)}.`
                : `Te weinig uren: ${this.formatHours(totalHours)} / vereist ${this.formatHours(requiredHours)}.`,
            details: [
                `Geregistreerd: ${this.formatHours(totalHours)}.`,
                `Vereist: ${this.formatHours(requiredHours)}.`,
                ...(!hoursOk ? [`Nog tekort: ${this.formatHours(requiredHours - totalHours)}.`] : []),
            ],
        });

        if (requiredSlots.length === 0) {
            stages.push({
                key: '3A',
                label: 'AI-validatie',
                passed: true,
                reason: 'Geen timesheet-validatie vereist voor deze entry.',
                details: ['Geen project in deze timesheet vraagt een verplichte timesheet-bijlage.'],
            });
        } else {
            const missingAi = requiredSlots.filter((slot: any) => !this.hasAnyAiValidationData(slot));
            const aiOk = missingAi.length === 0;
            stages.push({
                key: '3A',
                label: 'AI-validatie',
                passed: aiOk,
                reason: aiOk
                    ? 'AI-validatie aanwezig voor alle verplichte timeslots.'
                    : `AI-validatie ontbreekt voor ${missingAi.length} verplichte timeslot(s).`,
                details: aiOk
                    ? [`AI-validatie gevonden op ${requiredSlots.length}/${requiredSlots.length} verplichte timeslot(s).`]
                    : this.limitDetails(missingAi.map((slot: any) => `${this.getProjectLabel(slot)}: AI-validatie ontbreekt.`)),
            });
        }

        if (requiredSlots.length === 0) {
            stages.push({
                key: '3B',
                label: 'Bijlage en inhoud',
                passed: true,
                reason: 'Geen verplichte timesheet-bijlage voor deze entry.',
                details: ['Er is geen verplichte PDF, urencontrole of handtekening nodig voor deze timesheet.'],
            });
        } else {
            const invalidSlots = requiredSlots.filter((slot: any) => !this.isRequiredSlotValidationOk(slot));
            const validationOk = invalidSlots.length === 0;
            stages.push({
                key: '3B',
                label: 'Bijlage en inhoud',
                passed: validationOk,
                reason: validationOk
                    ? 'Verplichte timesheet-bijlagen en validatie zijn OK.'
                    : `Verplichte bijlage/validatie niet OK voor ${invalidSlots.length} timeslot(s).`,
                details: validationOk
                    ? [`PDF, gevalideerde uren en eventuele handtekening zijn OK voor ${requiredSlots.length} verplichte timeslot(s).`]
                    : this.limitDetails(this.collectDetails(invalidSlots, (slot: any) => this.getRequiredSlotValidationIssues(slot))),
            });
        }

        if (billableSlots.length === 0) {
            stages.push({
                key: '4',
                label: 'Facturatiegegevens',
                passed: true,
                reason: 'Geen factureerbare timeslots in deze entry.',
                details: ['Er zijn geen billable projecten in deze timesheet.'],
            });
        } else {
            const missingRateOrContact = billableSlots.filter((slot: any) => !this.hasRateAndContact(slot, timesheet));
            const pricingOk = missingRateOrContact.length === 0;
            stages.push({
                key: '4',
                label: 'Facturatiegegevens',
                passed: pricingOk,
                reason: pricingOk
                    ? 'Uurtarief en contact aanwezig voor factureerbare timeslots.'
                    : `Uurtarief en/of contact ontbreekt voor ${missingRateOrContact.length} factureerbare timeslot(s).`,
                details: pricingOk
                    ? [`Comanage contact en uurtarief zijn ingevuld voor ${billableSlots.length} factureerbare timeslot(s).`]
                    : this.limitDetails(this.collectDetails(missingRateOrContact, (slot: any) => this.getRateAndContactIssues(slot, timesheet))),
            });
        }

        return stages;
    }

    getReadyStageTooltipLabel(stage: ReadyToInvoiceStage): string {
        const state = stage?.passed ? 'In orde' : 'Niet in orde';
        return `${stage?.key}. ${stage?.label}: ${state}. ${stage?.reason || ''}`.trim();
    }

    private hasAnyAiValidationData(slot: any): boolean {
        return !!(
            slot?.validatedConsultantName ||
            slot?.validatedTimesheetType ||
            slot?.validatedHours !== null && slot?.validatedHours !== undefined ||
            slot?.validatedSigned !== null && slot?.validatedSigned !== undefined
        );
    }

    private isRequiredSlotValidationOk(slot: any): boolean {
        if (!slot?.pdfUrl) {
            return false;
        }

        const validatedHours = Number(slot?.validatedHours);
        const expectedHours = this.getExpectedHoursForSlot(slot);
        const hasHours = slot?.validatedHours !== null && slot?.validatedHours !== undefined && !isNaN(validatedHours);
        const hoursOk = hasHours && Math.abs(validatedHours - expectedHours) < 0.51;

        const signRequired = slot?.project?.signedTimesheetRequired === true;
        const signOk = !signRequired || slot?.validatedSigned === true;

        return hoursOk && signOk;
    }

    private getExpectedHoursForSlot(slot: any): number {
        const workedHours = (Number(slot?.minutes || 0) + Number(slot?.leaveMinutes || 0)) / 60;
        if (slot?.project?.invoiceByDay) {
            const daysWorked = workedHours / 8;
            return Number((daysWorked * (slot?.project?.hoursPerDay ?? 8)).toFixed(2));
        }
        return Number(workedHours.toFixed(2));
    }

    private hasRateAndContact(slot: any, timesheet: Timesheet): boolean {
        const project = this.getFullProject(slot?.project?.uuid) || slot?.project;
        const hasContact = !!project?.comanageContactId;

        const employeeUuid = timesheet?.employee?.uuid;
        const links: any[] = (project?.defaultLinkedEmployees || project?.linkedEmployees || []) as any[];
        const link = links.find((candidate: any) => {
            const candidateEmployeeUuid = candidate?.employeeId || candidate?.employee?.uuid;
            return candidateEmployeeUuid && employeeUuid && candidateEmployeeUuid === employeeUuid;
        });

        const hasRate = Number(link?.pricePerHour || 0) > 0;
        return hasContact && hasRate;
    }

    private getRequiredSlotValidationIssues(slot: any): string[] {
        const issues: string[] = [];
        const projectLabel = this.getProjectLabel(slot);

        if (!slot?.pdfUrl) {
            issues.push(`${projectLabel}: PDF-bijlage ontbreekt.`);
        }

        const expectedHours = this.getExpectedHoursForSlot(slot);
        const validatedHours = Number(slot?.validatedHours);
        const hasHours = slot?.validatedHours !== null && slot?.validatedHours !== undefined && !isNaN(validatedHours);
        if (!hasHours) {
            issues.push(`${projectLabel}: gevalideerde uren ontbreken.`);
        } else if (Math.abs(validatedHours - expectedHours) >= 0.51) {
            issues.push(`${projectLabel}: gevalideerde uren ${this.formatHours(validatedHours)} matchen niet met verwacht ${this.formatHours(expectedHours)}.`);
        }

        if (slot?.project?.signedTimesheetRequired === true && slot?.validatedSigned !== true) {
            issues.push(`${projectLabel}: handtekening ontbreekt of is niet gevalideerd.`);
        }

        return issues;
    }

    private getRateAndContactIssues(slot: any, timesheet: Timesheet): string[] {
        const project = this.getFullProject(slot?.project?.uuid) || slot?.project;
        const issues: string[] = [];
        const projectLabel = project?.title || this.getProjectLabel(slot);

        if (!project?.comanageContactId) {
            issues.push(`${projectLabel}: Comanage contact ontbreekt.`);
        }

        const employeeUuid = timesheet?.employee?.uuid;
        const links: any[] = (project?.defaultLinkedEmployees || project?.linkedEmployees || []) as any[];
        const link = links.find((candidate: any) => {
            const candidateEmployeeUuid = candidate?.employeeId || candidate?.employee?.uuid;
            return candidateEmployeeUuid && employeeUuid && candidateEmployeeUuid === employeeUuid;
        });

        if (Number(link?.pricePerHour || 0) <= 0) {
            const employeeName = [timesheet?.employee?.firstName, timesheet?.employee?.lastName].filter(Boolean).join(' ').trim() || 'medewerker';
            issues.push(`${projectLabel}: uurtarief ontbreekt voor ${employeeName}.`);
        }

        return issues;
    }

    private getFullProject(projectUuid?: string): Project | null {
        if (!projectUuid) {
            return null;
        }
        return (this.allProjects || []).find((project) => project.uuid === projectUuid) || null;
    }

    private getProjectLabel(slot: any): string {
        return slot?.project?.title || 'Onbekend project';
    }

    private formatHours(hours: number): string {
        return `${Math.max(0, Number(hours || 0)).toFixed(2)}u`;
    }

    private limitDetails(details: string[]): string[] {
        const uniqueDetails = Array.from(new Set(details.filter((detail) => !!detail)));
        const maxVisibleDetails = 5;
        if (uniqueDetails.length <= maxVisibleDetails) {
            return uniqueDetails;
        }
        return uniqueDetails.slice(0, maxVisibleDetails).concat(`Nog ${uniqueDetails.length - maxVisibleDetails} item(s)...`);
    }

    private collectDetails<T>(items: T[], getDetails: (item: T) => string[]): string[] {
        return (items || []).reduce((result: string[], item: T) => result.concat(getDetails(item)), []);
    }

    applyFilter(filterValue) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    getStatusTagClass(row: Timesheet): string {
        const key = row?.status?.key || '';
        if (key === 'DONE') {
            return 'tag tag--green';
        }
        return key === 'REVIEW' ? 'tag tag--red' : 'tag tag--blue';
    }

    getUniqueTimesheetType(row: Timesheet): string | null {
        const types = new Set<string>();
        (row?.timeslots || []).forEach((slot: any) => {
            const t = slot?.project?.timesheetType;
            if (t && typeof t === 'string' && t.trim().length > 0) {
                types.add(t.trim());
            }
        });
        if (types.size === 1) {
            return Array.from(types)[0];
        }
        return null;
    }


    getTimesheetType(row: Timesheet): string | null {
        const slots = Array.isArray(row?.timeslots) ? row.timeslots : [];
        const types = new Set<string>();
        for (const s of slots) {
            const req = s?.project?.timesheetRequired === true;
            const type = (s?.project?.timesheetType || '').toString().trim();
            if (req && type.length > 0) {
                types.add(type);
            }
        }
        if (types.size === 1) {
            return Array.from(types)[0];
        }
        return null;
    }

    addNewTimesheet() {
        const employees = [];
        this.timesheets.forEach((el) => employees.push(el.employee));
        this.logger.debug('Opening addTimesheetModal');
        const modalRef = this.modalService.open(AddTimesheetModalComponent, { windowClass: 'modal-prompt' });
        modalRef.componentInstance.currentEmployees = employees;
    }

    sendReminders() {
        this.sendingReminders = true;
        this.subscriptions.add(
            this.selectedDate$.subscribe((selectedDate) => {
                this.logger.debug('Attempt to send reminders');
                this.timesheetService.sendReminders(selectedDate).subscribe(
                    (result) => {
                        this.sendingReminders = false;
                        this.logger.debug('Succesfully sent reminders');
                        this.toastr.success('Reminders where sent successfully.');
                    },
                    (err) => {
                        this.sendingReminders = false;
                        this.logger.error('Failed to send reminders');
                        this.toastr.error('Oops, something went wrong. Please try again later.');
                    },
                );
            }),
        );
    }

    openCronjobSettings() {
        this.logger.debug('Opening cronjobRulesSettingsModal');
        const modalRef = this.modalService.open(CronjobRulesSettingsModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
    }

    deleteTimesheet(timesheet: Timesheet) {
        this.logger.debug('starting remove timesheet flow');
        const modalRef = this.modalService.open(ConfirmationModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.type = 'Delete';
        modalRef.componentInstance.title = 'Delete timesheet';
        modalRef.componentInstance.message =
            'Are you sure you want to delete ' + timesheet.employee.firstName + "'s timesheet? \n All linked timeslots will be removed allong with it.";

        this.subscriptions.add(
            modalRef.componentInstance.closeEvent.subscribe((val) => {
                if (val) {
                    this.logger.debug('attempting to remove timesheet');
                    this.store.dispatch(new RemoveTimesheet(timesheet.uuid)).subscribe(
                        () => {
                            this.logger.debug('successfully removed timesheet');
                            this.toastr.info('Timesheet was removed successfully.');
                        },
                        (error) => {
                            this.logger.error(error);
                            this.toastr.error('Oops, something went wrong please try again later.');
                        },
                    );
                }
            }),
        );
    }

    changeStatus(status: string, timesheet: Timesheet) {
        this.subscriptions.add(
            this.store.dispatch(new ChangeTimesheetStatus(timesheet.uuid, TIMESHEET_STATUS.getStatus(status))).subscribe(
                () => {
                    this.logger.debug('Succesfully updated timesheet');
                },
                (error) => {
                    this.logger.error('Failed to update timesheet');
                }
            ),
        );
    }

    copyUrlToClipboard(timesheet: Timesheet) {
        this.clipboard.copy(timesheet.url);
    }

        isReviewStatus(timesheet?: any): boolean {
                const rawStatus = timesheet?.status?.key ?? timesheet?.status?.displayName ?? timesheet?.status ?? '';
                return rawStatus.toString().trim().toUpperCase() === 'REVIEW';
        }

syncWithJira(timesheet: Timesheet) {
  if (!timesheet?.uuid) {
    this.toastr.error('Timesheet ID ontbreekt.');
    return;
  }

    if (this.isReviewStatus(timesheet)) {
        this.toastr.warning('Jira sync is disabled when the timesheet is in review.', 'Jira');
        return;
    }

  const fullName = `${timesheet.employee?.firstName ?? ''} ${timesheet.employee?.lastName ?? ''}`.trim();
  this.logger.debug('Jira sync gestart voor timesheet', timesheet.uuid);
  this.toastr.info(`Sync gestart voor ${fullName}`, 'Jira');

  this.subscriptions.add(
    this.timesheetsService.syncJira(timesheet.uuid)
      .pipe(
        finalize(() => {
          this.logger.debug('Jira sync gestopt voor timesheet', timesheet.uuid);
        })
      )
      .subscribe({
        next: () => {
          this.logger.debug('Jira sync succesvol', timesheet.uuid);
          this.toastr.success(`Sync voltooid voor ${fullName}`, 'Jira');

          //  Lijst refreshen
          this.subscriptions.add(
            this.selectedDate$.subscribe((selectedDate) => {
              this.logger.debug('Timesheets opnieuw laden na Jira sync...');
              this.ngOnInit();
              window.location.reload()
            })
          );
        },
        error: (err) => {
          this.logger.error('Jira sync mislukt', err);
          this.toastr.error('Sync met Jira is mislukt. Probeer later opnieuw.', 'Jira');
        }
      })
  );
}


    sendReminder(timesheet: Timesheet) {
        this.subscriptions.add(this.timesheetService.sendReminder(timesheet.uuid).subscribe());
    }

    getTotalMinutes = Timesheet.getTotalMinutes;
    getTotalDaysFromTimesheet = Timesheet.getTotalDaysFromTimesheet;
    getTotalDaysFromMinutes = Timesheet.getTotalDaysFromMinutes;
    getRequiredHoursDiff = Timesheet.getRequiredHoursDiff;
    getRequiredHoursDiffPercentage = Timesheet.getRequiredHoursDiffPercentage;
    getEffectiveRequiredHours = Timesheet.getEffectiveRequiredHours;

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openTimesheetDetailModal(timesheet: Timesheet) {
        const modalRef = this.modalService.open(TimesheetInfoModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
        modalRef.componentInstance.timesheet = timesheet;
    }

    departments = [
        { key: 'CONSULTANCY', label: 'Consultancy' },
        { key: 'WEBSITE',     label: 'Websites'    },
        { key: 'CUSTOM',      label: 'Custom (SOM)' },
        { key: 'HOSTING',     label: 'Hosting'       },
        { key: 'INTERN',      label: 'Intern'       },
        { key: 'OTHER',       label: 'Other'       },
      ];

    getSortedTimeslots(row) {
        return row.timeslots.slice().sort((a, b) => {
            const titleA = a.project?.title?.toLowerCase() ?? '';
            const titleB = b.project?.title?.toLowerCase() ?? '';
            return titleA.localeCompare(titleB);
        });
    }

    // Collect all non-empty comments for a timesheet row, including the timesheet-level note (extraInfo)
    getComments(row: any): Array<{ project: string; description: string }> {
        const comments: Array<{ project: string; description: string }> = [];

        // Timesheet-level comment
        const extraInfo = (row?.extraInfo ?? '').toString().trim();
        if (extraInfo.length > 0) {
            comments.push({ project: 'Timesheet', description: extraInfo });
        }

        // Timeslot-level comments
        const timeslotComments = (row?.timeslots ?? [])
            .filter((slot: any) => !!slot?.description && String(slot.description).trim().length > 0)
            .map((slot: any) => ({
                project: slot.project?.title ?? '—',
                description: String(slot.description).trim(),
            }));

        return comments
            .concat(timeslotComments)
            .sort((a, b) => a.project.localeCompare(b.project));
    }

    // Preview: show multiple comments concatenated, truncated for table cell
    getCommentsPreview(row: any): string {
        const comments = this.getComments(row).map((c) => c.description);
        if (comments.length === 0) return '—';
        // take the first few comments and join
        const joined = comments.slice(0, 3).join(' | ');
        // truncate to keep the cell compact
        const max = 120;
        return joined.length > max ? joined.substring(0, max - 1) + '…' : joined;
    }

    getTimesheetPercentage(
        type: 'CUSTOM' | 'CONSULTANCY' | 'OTHER' | 'WEBSITE' | 'HOSTING' | 'INTERN',
        timeslots: { minutes: number; project: { projectType: string } }[]
      ): number {
        // 1. Totaal aantal gewerkte minuten
        const totalMinutes = timeslots.reduce((sum, slot) => sum + slot.minutes, 0);
      
        // 2. Bij gebrek aan data => 0%
        if (totalMinutes === 0) {
          return 0;
        }
      
        // 3. Minuten voor het opgegeven projectType
        const typeMinutes = timeslots
          .filter(slot => slot.project.projectType === type)
          .reduce((sum, slot) => sum + slot.minutes, 0);
      
        // 4. Bereken percentage en rond af op 2 decimalen
        const rawPct = (typeMinutes / totalMinutes) * 100;
        return Math.round(rawPct * 100) / 100;
      }

      getTimesheetPercentageTotals(type: string): number {
        // 1. Haal alle timeslots uit de verschillende timesheets
        const allSlots = this.timesheets.reduce(
          (acc: any[], ts: any) => acc.concat(ts.timeslots),
          []
        );
      
        // 2. Bereken totaal aantal minuten
        const totalMinutes = allSlots.reduce(
          (sum: number, slot: any) => sum + slot.minutes,
          0
        );
      
        // 3. Bereken aantal minuten voor het gevraagde type
        const typeMinutes = allSlots
          .filter((slot: any) => slot.project.projectType === type)
          .reduce((sum: number, slot: any) => sum + slot.minutes, 0);
      
        // 4. Reken om naar percentage en rond af op 2 decimalen
        if (totalMinutes === 0) {
          return 0;
        }
        const percentage = (typeMinutes / totalMinutes) * 100;
        return Math.round(percentage * 100) / 100;
    }

        // Build over/under hours report entries
        getOverUnderEntries(): Array<{
                employee: string;
                totalHours: number;
                requiredHours: number;
                differenceHours: number;
                type: 'Overuren' | 'Onderuren';
            }> {
                if (!this.timesheets) return [];
                return this.timesheets
                    .map((ts) => {
                        const totalHours = Math.round((Timesheet.getTotalMinutes(ts) / 60) * 100) / 100;
                        const requiredHours = ts.hoursRequired ?? 0;
                        const differenceHours = Math.round((totalHours - requiredHours) * 100) / 100;
                        return {
                            employee: `${ts.employee?.firstName ?? ''} ${ts.employee?.lastName ?? ''}`.trim(),
                            totalHours,
                            requiredHours,
                            differenceHours,
                              type: (differenceHours > 0 ? 'Overuren' : 'Onderuren') as 'Overuren' | 'Onderuren',
                        };
                    })
                    .filter((e) => e.differenceHours !== 0)
                    .sort((a, b) => Math.abs(b.differenceHours) - Math.abs(a.differenceHours));
            }

    openReportsModal(type: 'departments' | 'overtime') {
        const modalRef = this.modalService.open(ReportsModalComponent, {
            windowClass: 'reports-modal--wide',
            size: 'xl',
            animation: false,
        });
        modalRef.componentInstance.reportType = type;
        modalRef.componentInstance.timesheets = this.timesheets;
        modalRef.componentInstance.departments = this.departments;
    }
}
